/* eslint-disable prettier/prettier */
import React from 'react';

import { useTheme } from 'styled-components';
import { ThemeType } from 'theme/theme';
import { ReactComponent as TicketIcon } from 'assets/images/private-fare.svg';
import { ReactComponent as FlightDuotone } from 'assets/images/flight-duotone.svg';
import { Desktop, Mobile } from 'components/Responsive';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { FlightGroupedResultResponse, FlightStretch } from 'models/flightSearch';
import { getFormattedPrice } from 'utils/currency';

import FlightOption from './FlightOption';
import {
  Card,
  FlightsContainer,
  Summary,
  RouteTypeWrapper,
  RouteTypeText,
  RouteTypeRoot,
  RouteTypeDate,
  RouteTypeHeaderContainer,
  RouteTypeHeaderText,
  FlightsListContainer,
  FinalPriceWithTaxes,
  PriceContainer,
  PriceWrapper,
  Price,
  Currency,
  ConfirmButton,
  FlightsWrapper,
  RouteTypeStartEndAirportContainer,
  RouteTypeCityName,
  SummaryMobile,
  ConfirmButtomMobile,
  FinalPriceWithTaxesMobile,
  BreakRulesMessage,
  NoBreakRulesMessage,
  PrivateFareMessage,
  ConfirmUpSellButton,
  ConfirmUpSellButtomMobile,
  FooterMobile,
  PrivateFareIconContainer,
} from './styles';
import { getFormattedRouteType } from './utils';

interface Props {
  data: FlightGroupedResultResponse;
  onClickWithUpSell: (selectedFlightsKeys: []) => void;
  testId?: string;
  departurePoint?: Airport;
  arrivalPoint?: Airport;
  departureDate?: Date;
  returnDate?: Date;
  onClickWithoutUpSell: (selectedFlightsKeys: []) => void;
  numberOfTravelers: number;
  flightStretch: FlightStretch;
}

const RouteType = ({ label, date, color }) => (
  <RouteTypeRoot>
    <RouteTypeWrapper>
      <FlightDuotone fill={color} />
      <RouteTypeText>{label}</RouteTypeText>
    </RouteTypeWrapper>
    <RouteTypeDate>{date}</RouteTypeDate>
  </RouteTypeRoot>
);

const RouteTypeStartEndAirport = ({ airport }: { airport?: Airport }) => (
  <RouteTypeStartEndAirportContainer>
    <RouteTypeHeaderText>{airport?.code}</RouteTypeHeaderText>
    <RouteTypeCityName title={airport?.related_city}>{airport?.related_city}</RouteTypeCityName>
  </RouteTypeStartEndAirportContainer>
);

const RouteTypeHeader = ({
  routeType,
  date,
  airports: [departureAirport, arrivalAirport],
  color,
}: {
  routeType: string;
  date: string;
  airports: [Airport?, Airport?];
  color: string;
}) => {
  const { t } = useStoreTranslation();

  return (
    <RouteTypeHeaderContainer>
      <RouteType label={routeType} date={date} color={color} />
      <Desktop>
        <RouteTypeStartEndAirport airport={departureAirport} />
        <div />
        <RouteTypeStartEndAirport airport={arrivalAirport} />
        <RouteTypeHeaderText>{t('flightsCard.luggage')}</RouteTypeHeaderText>
      </Desktop>
    </RouteTypeHeaderContainer>
  );
};

const CardGroupedFlights = ({
  data,
  onClickWithUpSell,
  testId,
  arrivalPoint,
  departureDate,
  departurePoint,
  returnDate,
  onClickWithoutUpSell,
  numberOfTravelers,
  flightStretch,
}: Props) => {
  const { t } = useStoreTranslation();
  const {
    sectors: [departureFlights, returnFlights],
    price: { total, currencyCode } = {
      total: 0,
      currencyCode: 'BRL',
    },
    breakRulesMessage,
    hasPrivateFare,
  } = data || {};

  const [newSelectedFlightKeys, setNewSelectedFlightKeys] = React.useState<string[]>(() => {
    const items = [];
    for (let index = 0; index < data?.sectors.length; index++) {
      const sector = data.sectors[index];
      items.push(sector?.options?.first().flightKeys);
    }
    return items;
  });

  const theme = useTheme() as ThemeType;

  const formattedPrice = (value: number, currencyCode: string) =>
    getFormattedPrice({ value, currency: currencyCode, t });

  //Price
  const formattedPriceTotal = formattedPrice(total, currencyCode);

  //AlternativePrice
  const formattedAlternativePriceTotal = data.alternativePrice
    ? formattedPrice(data.alternativePrice.total, data.alternativePrice.currencyCode)
    : undefined;

  const handleSelectedFlightKeys = (sectorNumber: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const tempFlightKeys = [...newSelectedFlightKeys];
    tempFlightKeys[sectorNumber - 1] = value;
    setNewSelectedFlightKeys(tempFlightKeys);
  };

  const handleSelectFlightWithoutUpSell = () => {
    onClickWithoutUpSell(newSelectedFlightKeys.map((s) => s?.replaceAll('#', ',')) as unknown as []);
  };

  const handleSelectFlightWithUpSell = () =>
    onClickWithUpSell(newSelectedFlightKeys.map((s) => s?.replaceAll('#', ',')) as unknown as []);

  return (
    <Card data-testid={testId}>
      <FlightsContainer>
        {/* Ida */}
        {flightStretch !== FlightStretch.MULTIDESTINATION && (
          <FlightsWrapper>
            <RouteTypeHeader
              routeType={t('flightsCard.routeTypeDeparture')}
              date={getFormattedRouteType(departureDate)}
              airports={[departurePoint, arrivalPoint]}
              color={theme.primaryBackgroundColor}
            />
            <FlightsListContainer value={newSelectedFlightKeys[0]} onChange={handleSelectedFlightKeys(1)}>
              {departureFlights?.options?.map((option) => {
                const { flightKeys } = option;
                return <FlightOption key={flightKeys} groupedSectorOption={option} />;
              })}
            </FlightsListContainer>
          </FlightsWrapper>
        )}

        {/* Si hay vuelta */}
        {flightStretch !== FlightStretch.MULTIDESTINATION && returnFlights && returnFlights?.options && (
          <FlightsWrapper>
            <RouteTypeHeader
              routeType={t('flightsCard.routeTypeReturn')}
              date={getFormattedRouteType(returnDate)}
              airports={[arrivalPoint, departurePoint]}
              color={theme.primaryBackgroundColor}
            />
            <FlightsListContainer value={newSelectedFlightKeys[1]} onChange={handleSelectedFlightKeys(2)}>
              {returnFlights?.options?.map((option) => {
                const { flightKeys } = option;
                return <FlightOption key={flightKeys} groupedSectorOption={option} />;
              })}
            </FlightsListContainer>
          </FlightsWrapper>
        )}

        {/* Si es Multidestinos */}
        {flightStretch === FlightStretch.MULTIDESTINATION && (
          <>
            {data.sectors.map((s, index) => (
              <>
                <FlightsWrapper>
                  <RouteTypeHeader
                    routeType={`${t('flightsSearch.sector')} ${s.sectorNumber}`}
                    date={getFormattedRouteType(s.options?.first()?.flights?.first()?.departureDate)}
                    airports={[
                      {
                        code: s.options?.first()?.flights?.first()?.departureAirport ?? '',
                        related_city: s.options?.first()?.flights?.first()?.departureCityName ?? '',
                      } as Airport,
                      {
                        code: s.options?.first()?.flights?.last()?.arrivalAirport ?? '',
                        related_city: s.options?.first()?.flights?.last()?.arrivalCityName ?? '',
                      } as Airport,
                    ]}
                    color={theme.primaryBackgroundColor}
                  />
                  <FlightsListContainer
                    value={newSelectedFlightKeys[s.sectorNumber - 1]}
                    onChange={handleSelectedFlightKeys(s.sectorNumber)}
                  >
                    {s?.options?.map((option) => {
                      const { flightKeys } = option;
                      return <FlightOption key={flightKeys} groupedSectorOption={option} />;
                    })}
                  </FlightsListContainer>
                </FlightsWrapper>
              </>
            ))}
          </>
        )}
      </FlightsContainer>
      <Desktop>
        <Summary>
          <PriceContainer>
            <PriceWrapper>
              <Price>{formattedPriceTotal?.formattedWithoutCode}</Price>
              <Currency>{formattedPriceTotal?.currency}</Currency>
            </PriceWrapper>

            {data.alternativePrice && (
              <PriceWrapper>
                {t('alternativePrice.or')}
                <Price>{formattedAlternativePriceTotal?.formattedWithoutCode}</Price>
                <Currency>{formattedAlternativePriceTotal?.currency}</Currency>
              </PriceWrapper>
            )}
          </PriceContainer>
          {hasPrivateFare && (
            <PrivateFareMessage>
              <PrivateFareIconContainer>
                <TicketIcon stroke={theme.primaryBackgroundColor} />
              </PrivateFareIconContainer>
              {t('flightDetails.privateFare')}
            </PrivateFareMessage>
          )}
          <FinalPriceWithTaxes>
            {t('flightsCard.finalPriceWithTaxes')} {t('flightsCard.toXPassengers', { count: numberOfTravelers ?? 1 })}
          </FinalPriceWithTaxes>
          {breakRulesMessage && breakRulesMessage?.length > 0 ? (
            <BreakRulesMessage>{t('flightDetails.outOfPolicies')}</BreakRulesMessage>
          ) : (
            <NoBreakRulesMessage>{t('flightDetails.withinPolicies')}</NoBreakRulesMessage>
          )}
          <ConfirmButton onClick={handleSelectFlightWithoutUpSell}>{t('flightsCard.confirmButton')}</ConfirmButton>
          {data.upSellEnabled && (
            <ConfirmUpSellButton value={t('flightsCard.confirmUpSellButton')} onClick={handleSelectFlightWithUpSell} />
          )}
        </Summary>
      </Desktop>
      <Mobile>
        <SummaryMobile>
          <PriceContainer>
            <PriceWrapper>
              <Price>{formattedPriceTotal?.formattedWithoutCode}</Price>
              <Currency>{formattedPriceTotal?.currency}</Currency>
            </PriceWrapper>

            {data.alternativePrice && (
              <PriceWrapper>
                {t('alternativePrice.or')}
                <Price>{formattedAlternativePriceTotal?.formattedWithoutCode}</Price>
                <Currency>{formattedAlternativePriceTotal?.currency}</Currency>
              </PriceWrapper>
            )}
            <FinalPriceWithTaxesMobile>{t('flightsCard.finalPriceWithTaxes')}</FinalPriceWithTaxesMobile>
            <FinalPriceWithTaxesMobile>
              {t('flightsCard.toXPassengers', { count: numberOfTravelers ?? 1 })}
            </FinalPriceWithTaxesMobile>
            {breakRulesMessage && breakRulesMessage?.length > 0 ? (
              <BreakRulesMessage>{t('flightDetails.outOfPolicies')}</BreakRulesMessage>
            ) : (
              <NoBreakRulesMessage>{t('flightDetails.withinPolicies')}</NoBreakRulesMessage>
            )}
          </PriceContainer>
          <ConfirmButtomMobile onClick={handleSelectFlightWithoutUpSell}>
            {t('flightsCard.confirmButton')}
          </ConfirmButtomMobile>
        </SummaryMobile>
        {data.upSellEnabled && (
          <FooterMobile>
            <ConfirmUpSellButtomMobile
              value={t('flightsCard.confirmUpSellButton')}
              onClick={handleSelectFlightWithUpSell}
            />
          </FooterMobile>
        )}
      </Mobile>
    </Card>
  );
};

export default CardGroupedFlights;
